import React, { useState } from 'react';
import { useStore } from '../store/useStore';
import { User } from '../types';
import { UserCircle2, Filter } from 'lucide-react';
import { AdvancedFilters } from './AdvancedFilters';
import { ThemeToggle } from './ThemeToggle';
import { initializeSocket } from '../services/socket';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

export const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const { setUser, advancedFilters } = useStore();
  const activeUsersCount = useStore((state) => state.activeUsersCount);
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    gender: 'male' as 'male' | 'female' | 'other',
    age: 25,
    interests: [] as string[],
    location: '',
    language: 'portuguese',
    preferences: {
      gender: ['female'] as ('male' | 'female' | 'other')[],
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const user: User = {
      id: Math.random().toString(36).substr(2, 9),
      name: formData.name,
      gender: formData.gender,
      age: formData.age,
      interests: advancedFilters.interests.length > 0 ? advancedFilters.interests : formData.interests,
      location: advancedFilters.location || formData.location,
      language: advancedFilters.language !== 'any' ? advancedFilters.language : formData.language,
      preferences: {
        gender: formData.preferences.gender,
        ageRange: advancedFilters.ageRange,
        interests: advancedFilters.interests,
        location: advancedFilters.location,
        language: advancedFilters.language,
      }
    };
    setUser(user);
    initializeSocket(user);
  };

  const handleInterestsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const interests = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
    setFormData({ ...formData, interests });
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 user-profile">
      <div className="bg-yellow-400 p-4 md:p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-center mb-4 md:mb-6">
          <UserCircle2 className="w-12 h-12 md:w-16 md:h-16 text-black" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-black text-center mb-2">
          Crazy CHAT
        </h2>
        <p className="text-center text-black/70 mb-6 font-medium">
          {activeUsersCount} {t('profile.online_users')}
        </p>
        <div className="flex justify-between items-center mb-2">
          <button
            type="button"
            onClick={() => setShowFilters(true)}
            className="p-2 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors"
          >
            <Filter className="w-5 h-5 text-white" />
          </button>
          <div className="flex space-x-2">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-black font-semibold mb-2">
              {t('profile.your_name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full p-2 rounded border-2 border-black focus:outline-none focus:border-black"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-black font-semibold mb-2">
                {t('profile.your_gender')}
              </label>
              <select
                value={formData.gender}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    gender: e.target.value as 'male' | 'female' | 'other',
                  })
                }
                className="w-full p-2 rounded border-2 border-black focus:outline-none focus:border-black"
              >
                <option value="male">{t('profile.male')}</option>
                <option value="female">{t('profile.female')}</option>
                <option value="other">{t('profile.other')}</option>
              </select>
            </div>
            <div>
              <label className="block text-black font-semibold mb-2">
                {t('profile.age') || 'Idade'}
              </label>
              <input
                type="number"
                min="18"
                max="100"
                value={formData.age}
                onChange={(e) =>
                  setFormData({ ...formData, age: parseInt(e.target.value) || 18 })
                }
                className="w-full p-2 rounded border-2 border-black focus:outline-none focus:border-black"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-black font-semibold mb-2">
              {t('profile.interests') || 'Seus interesses'}
            </label>
            <input
              type="text"
              value={formData.interests.join(', ')}
              onChange={handleInterestsChange}
              placeholder={t('profile.interests_placeholder') || 'Ex: música, games, viagens'}
              className="w-full p-2 rounded border-2 border-black focus:outline-none focus:border-black"
            />
          </div>
          <div>
            <label className="block text-black font-semibold mb-2">
              {t('profile.looking_for')}
            </label>
            <div className="space-y-2">
              {(['male', 'female', 'other'] as const).map((gender) => (
                <label key={gender} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.preferences.gender.includes(gender)}
                    onChange={(e) => {
                      const newGenders = e.target.checked
                        ? [...formData.preferences.gender, gender]
                        : formData.preferences.gender.filter((g) => g !== gender);
                      setFormData({
                        ...formData,
                        preferences: { ...formData.preferences, gender: newGenders },
                      });
                    }}
                    className="mr-2"
                  />
                  <span className="capitalize">{t(`profile.${gender}`)}</span>
                </label>
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-black text-yellow-400 py-2 px-4 rounded font-bold hover:bg-gray-800 transition-colors"
          >
            {t('profile.start_chatting')}
          </button>
        </form>
        <AdvancedFilters
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApplyFilters={(filters) => console.log('Filtros aplicados:', filters)}
        />
      </div>
    </div>
  );
};
